import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import { homeUrl, profileUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Setting property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'Always Public' : null))
const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => !value)
const getCantUndoFn = () => () => true
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// DOM Actions

async function getHasCoverPhoto(): Promise<boolean> {
  const editCoverPhotoButton = await promises.getWaitForElementToAppearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//*[${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'edit cover photo',
        )}]`,
      )[0],
    2000,
  )

  const addCoverPhotoButton = await promises.getWaitForElementToAppearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        `//*[${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'add cover photo',
        )}]`,
      )[0],
    2000,
  )

  return Boolean(editCoverPhotoButton && !addCoverPhotoButton)
}

function openCoverPhotoSubmenu(): Promise<Node> | null {
  const editCoverPhotoButton = evaluateXpath<HTMLElement>(
    document,
    `//*[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'edit cover photo',
    )}]`,
  )[0]

  if (!editCoverPhotoButton) {
    throw new Error("Could not find element for 'Edit cover photo' button")
  }

  const isSubmenuVisible = () =>
    evaluateXpath<HTMLElement>(document, '//*[text() = "Remove"]').length > 0
  const submenuListener = promises.getDomPromise(document, isSubmenuVisible)

  editCoverPhotoButton.click()

  return submenuListener
}

function openRemoveCoverPhotoConfirmationModal(): Promise<Node> | null {
  const removeMenuItem = evaluateXpath<HTMLElement>(
    document,
    '//*[text() = "Remove"]',
  )[0]

  if (!removeMenuItem) {
    throw new Error("Could not find element for 'Remove' menu item")
  }

  const isConfirmationModalVisible = () =>
    evaluateXpath<HTMLElement>(document, '//*[text() = "Confirm"]').length > 0
  const modalListener = promises.getDomPromise(
    document,
    isConfirmationModalVisible,
  )

  removeMenuItem.click()

  return modalListener
}

function confirmCoverPhotoRemoval(): Promise<Node> | null {
  const confirmButton = evaluateXpath<HTMLElement>(
    document,
    '//*[text() = "Confirm"]',
  )[0]

  if (!confirmButton) {
    throw new Error(
      "Could not find element for 'Confirm' button to remove cover photo",
    )
  }

  const isConfirmationModalClosed = () =>
    evaluateXpath<HTMLElement>(document, '//*[text() = "Confirm"]').length === 0
  const modalListener = promises.getDomPromise(
    document,
    isConfirmationModalClosed,
  )

  confirmButton.click()

  return modalListener
}

function cancelCoverPhotoRemoval(): Promise<Node> | null {
  const cancelButton = evaluateXpath<HTMLElement>(
    document,
    '//*[text() = "Cancel"]',
  )[0]

  if (!cancelButton) {
    throw new Error("Could not find 'Cancel' button for cover photo removal")
  }

  const isConfirmationModalClosed = () =>
    evaluateXpath<HTMLElement>(document, '//*[text() = "Confirm"]').length === 0
  const modalListener = promises.getDomPromise(
    document,
    isConfirmationModalClosed,
  )

  cancelButton.click()

  return modalListener
}

enum StageKey {
  GATHER_COVER_PHOTO_INTIALIZE = 'GATHER_COVER_PHOTO_INTIALIZE',
  GATHER_COVER_PHOTO_CURRENT_STATUS = 'GATHER_COVER_PHOTO_CURRENT_STATUS',
  UPDATE_COVER_PHOTO_INITIALIZE = 'UPDATE_COVER_PHOTO_INITIALIZE',
  UPDATE_REMOVE_COVER_PHOTO = 'UPDATE_COVER_PHOTO',
}

// An initial stage that ensures this automation starts from the home page
// Facebook profile urls may have the username which can be anything as the path
// making it impossible to set a specific url pattern. Starting on the home url
// ensures we do the first navigation since it has no path and fails the match.
const initStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_COVER_PHOTO_INTIALIZE,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: homeUrl,
    action: async () => {},
  },
]

const gatherStageDefs: StageDef[] = [
  ...initStageDefs,
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_COVER_PHOTO_CURRENT_STATUS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: profileUrl,
    urlPattern: `${homeUrl}/.+`,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      const hasCoverPhoto = await getHasCoverPhoto()

      await updateStoredInitialValues(
        { [FacebookSettingKey.CURRENT_COVER_PHOTO]: hasCoverPhoto },
        FBRecommendationKey.FB_CURRENT_COVER_PHOTO,
      )
    },
  },
]

// Execution

const updateStageDefs: StageDef[] = [
  ...initStageDefs,
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_REMOVE_COVER_PHOTO,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: profileUrl,
    urlPattern: `${homeUrl}/.+`,
    action: async (machineContext, userContext) => {
      const hasCoverPhoto = await getHasCoverPhoto()

      if (!hasCoverPhoto) {
        return false
      }

      await openCoverPhotoSubmenu()
      await openRemoveCoverPhotoConfirmationModal()

      if (userContext?.isDry) {
        await cancelCoverPhotoRemoval()
      } else {
        await confirmCoverPhotoRemoval()
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
